import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField, 
  Grid, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Autocomplete,
  Alert,
  Breadcrumbs,
  Link
} from '@mui/material';
import { 
  Save as SaveIcon, 
  ArrowBack as ArrowBackIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ptBR from 'date-fns/locale/pt-BR';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import AuthLayout from '../components/AuthLayout';
import costService from '../services/cost.service';
import serviceRequestService from '../services/serviceRequest.service';
import commercialVisitService from '../services/commercialVisit.service';
import userService from '../services/user.service';
import { CostFormValues, COST_CATEGORY_LABELS, COST_TYPE_LABELS } from '../types/cost.types';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { ServiceRequestType } from '../types/serviceRequest.types';
import { CommercialVisitType } from '../types/commercialVisit.types';

const CostForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { showSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  const isEditMode = id !== 'new';
  
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(id ? true : false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  
  // Estados para referências
  const [serviceRequests, setServiceRequests] = useState<ServiceRequestType[]>([]);
  const [commercialVisits, setCommercialVisits] = useState<CommercialVisitType[]>([]);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [loadingServiceRequests, setLoadingServiceRequests] = useState<boolean>(false);
  const [loadingCommercialVisits, setLoadingCommercialVisits] = useState<boolean>(false);
  const [loadingTechnicians, setLoadingTechnicians] = useState<boolean>(false);
  
  // Esquema de validação
  const validationSchema = Yup.object({
    costType: Yup.string().required('Tipo de custo é obrigatório'),
    category: Yup.string().required('Categoria é obrigatória'),
    description: Yup.string().required('Descrição é obrigatória'),
    amount: Yup.number().required('Valor é obrigatório').min(0, 'Valor deve ser maior ou igual a zero'),
    date: Yup.date().required('Data é obrigatória'),
    serviceRequest: Yup.string().when('costType', {
      is: 'technical_direct',
      then: (schema) => schema.required('Chamado técnico é obrigatório para custos diretos técnicos')
    }),
    commercialVisit: Yup.string().when('costType', {
      is: 'commercial_direct',
      then: (schema) => schema.required('Visita comercial é obrigatória para custos diretos comerciais')
    }),
    hourlyRate: Yup.number().min(0, 'Taxa horária deve ser maior ou igual a zero'),
    hoursWorked: Yup.number().min(0, 'Horas trabalhadas deve ser maior ou igual a zero'),
    kilometers: Yup.number().min(0, 'Quilômetros deve ser maior ou igual a zero'),
    costPerKilometer: Yup.number().min(0, 'Custo por quilômetro deve ser maior ou igual a zero'),
    quantity: Yup.number().min(0, 'Quantidade deve ser maior ou igual a zero'),
    unitCost: Yup.number().min(0, 'Custo unitário deve ser maior ou igual a zero'),
    notes: Yup.string()
  });
  
  // Valores iniciais do formulário
  const initialValues: CostFormValues = {
    costType: 'technical_direct',
    // @ts-ignore - Valor vazio não é atribuível ao tipo esperado
    category: '',
    description: '',
    amount: 0,
    date: new Date().toISOString(),
    serviceRequest: '',
    commercialVisit: '',
    technician: '',
    hourlyRate: undefined,
    hoursWorked: undefined,
    kilometers: undefined,
    costPerKilometer: undefined,
    quantity: undefined,
    unitCost: undefined,
    notes: '',
    attachments: []
  };
  
  // Formik
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        
        if (id && id !== 'new') {
          // Modo de edição
          await costService.updateCost(id, values);
          showSnackbar('Custo atualizado com sucesso!', 'success');
        } else {
          // Modo de criação
          await costService.createCost(values);
          showSnackbar('Custo criado com sucesso!', 'success');
        }
        
        // Redirecionar para a lista de custos
        navigate('/costs');
      } catch (error: any) {
        console.error('Erro ao salvar custo:', error);
        showSnackbar(
          error.response?.data?.message || 'Erro ao salvar custo. Tente novamente.',
          'error'
        );
      } finally {
        setLoading(false);
      }
    },
  });
  
  // Carregar custo existente se estiver no modo de edição
  const loadCost = async () => {
    try {
      setInitialLoading(true);
      
      if (id && id !== 'new') {
        const cost = await costService.getCostById(id);
        
        // Preencher o formulário com os dados do custo
        formik.setValues({
          ...formik.values,
          ...cost,
          // Garantir que os campos específicos estejam presentes
          serviceRequest: cost.serviceRequest?._id || cost.serviceRequest || '',
          commercialVisit: cost.commercialVisit?._id || cost.commercialVisit || '',
        });
      }
    } catch (error: any) {
      console.error('Erro ao carregar custo:', error);
      showSnackbar(
        error.response?.data?.message || 'Erro ao carregar custo. Tente novamente.',
        'error'
      );
    } finally {
      setInitialLoading(false);
    }
  };
  
  // Carregar chamados técnicos para referência
  const loadServiceRequests = async () => {
    try {
      setLoadingServiceRequests(true);
      
      // Usar o serviço que agora é mais robusto e lida com diferentes formatos de resposta
      const response = await serviceRequestService.getServiceRequests();
      
      // Filtrar apenas os chamados que não estão cancelados ou concluídos
      const activeRequests = response.docs.filter(req => 
        req.status !== 'cancelled' && req.status !== 'completed'
      );
      
      console.log('Chamados ativos carregados:', activeRequests.length);
      // @ts-ignore - Incompatibilidade de tipos entre ServiceRequestType[] e SetStateAction<ServiceRequestType[]>
      setServiceRequests(activeRequests || []);
    } catch (error: any) {
      console.error('Erro ao carregar chamados técnicos:', error);
      showSnackbar(
        'Erro ao carregar chamados técnicos. Alguns campos podem não estar disponíveis.',
        'warning'
      );
      setServiceRequests([]); // Garantir que o estado seja atualizado mesmo em caso de erro
    } finally {
      setLoadingServiceRequests(false);
    }
  };
  
  // Carregar visitas comerciais para referência
  const loadCommercialVisits = async () => {
    try {
      setLoadingCommercialVisits(true);
      
      // Buscar visitas comerciais
      const response = await commercialVisitService.getCommercialVisits({ limit: 1000 });
      
      // Extrair o array de visitas do objeto de resposta
      let visitsList: CommercialVisitType[] = [];
      if (response.visits && Array.isArray(response.visits)) {
        visitsList = response.visits;
      } else if (Array.isArray(response)) {
        visitsList = response;
      } else {
        console.error('Formato de resposta inesperado do serviço de visitas comerciais:', response);
      }
      
      console.log('Visitas comerciais carregadas:', visitsList.length);
      setCommercialVisits(visitsList);
    } catch (error: any) {
      console.error('Erro ao carregar visitas comerciais:', error);
      showSnackbar(
        'Erro ao carregar visitas comerciais. Alguns campos podem não estar disponíveis.',
        'warning'
      );
      setCommercialVisits([]); // Garantir que o estado seja atualizado mesmo em caso de erro
    } finally {
      setLoadingCommercialVisits(false);
    }
  };
  
  // Carregar técnicos disponíveis
  const loadTechnicians = async () => {
    try {
      setLoadingTechnicians(true);
      
      // Usar a função getAvailableTechnicians para buscar técnicos
      const techniciansList = await userService.getAvailableTechnicians();
      
      if (Array.isArray(techniciansList)) {
        setTechnicians(techniciansList);
      } else {
        console.error('Formato de resposta inesperado do serviço de técnicos:', techniciansList);
        setTechnicians([]);
      }
    } catch (error: any) {
      console.error('Erro ao carregar técnicos:', error);
      showSnackbar(
        'Erro ao carregar técnicos. Alguns campos podem não estar disponíveis.',
        'warning'
      );
      setTechnicians([]); // Garantir que o estado seja atualizado mesmo em caso de erro
    } finally {
      setLoadingTechnicians(false);
    }
  };
  
  // Calcular valor com base nos campos específicos
  const calculateAmount = () => {
    const { costType, category, hourlyRate, hoursWorked, kilometers, costPerKilometer, quantity, unitCost } = formik.values;
    
    // @ts-ignore - Tipos não têm sobreposição
    if (costType === 'technical_direct' && category === 'labor' && hourlyRate && hoursWorked) {
      formik.setFieldValue('amount', hourlyRate * hoursWorked);
    } else if (costType === 'technical_direct' && category === 'transportation' && kilometers && costPerKilometer) {
      formik.setFieldValue('amount', kilometers * costPerKilometer);
    // @ts-ignore - Tipos não têm sobreposição
    } else if ((costType === 'technical_direct' || costType === 'commercial_direct') && category === 'material' && quantity && unitCost) {
      formik.setFieldValue('amount', quantity * unitCost);
    }
  };
  
  // Renderizar campos específicos com base no tipo de custo e categoria
  const renderSpecificFields = () => {
    const { costType, category } = formik.values;
    
    // @ts-ignore - Tipos não têm sobreposição
    if (costType === 'technical_direct' && category === 'labor') {
      return (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Cálculo de Mão de Obra
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="hourlyRate"
                  name="hourlyRate"
                  label="Taxa Horária (R$/hora)"
                  type="number"
                  value={formik.values.hourlyRate || ''}
                  onChange={formik.handleChange}
                  error={formik.touched.hourlyRate && Boolean(formik.errors.hourlyRate)}
                  helperText={formik.touched.hourlyRate && formik.errors.hourlyRate}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="hoursWorked"
                  name="hoursWorked"
                  label="Horas Trabalhadas"
                  type="number"
                  value={formik.values.hoursWorked || ''}
                  onChange={formik.handleChange}
                  error={formik.touched.hoursWorked && Boolean(formik.errors.hoursWorked)}
                  helperText={formik.touched.hoursWorked && formik.errors.hoursWorked}
                  InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<CalculateIcon />}
                  onClick={calculateAmount}
                  disabled={!formik.values.hourlyRate || !formik.values.hoursWorked}
                >
                  Calcular Valor
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      );
    }
    
    if (costType === 'technical_direct' && category === 'transportation') {
      return (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Cálculo de Transporte
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="kilometers"
                  name="kilometers"
                  label="Quilômetros Percorridos"
                  type="number"
                  value={formik.values.kilometers || ''}
                  onChange={formik.handleChange}
                  error={formik.touched.kilometers && Boolean(formik.errors.kilometers)}
                  helperText={formik.touched.kilometers && formik.errors.kilometers}
                  InputProps={{ inputProps: { min: 0, step: 0.1 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="costPerKilometer"
                  name="costPerKilometer"
                  label="Custo por Quilômetro (R$/km)"
                  type="number"
                  value={formik.values.costPerKilometer || ''}
                  onChange={formik.handleChange}
                  error={formik.touched.costPerKilometer && Boolean(formik.errors.costPerKilometer)}
                  helperText={formik.touched.costPerKilometer && formik.errors.costPerKilometer}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<CalculateIcon />}
                  onClick={calculateAmount}
                  disabled={!formik.values.kilometers || !formik.values.costPerKilometer}
                >
                  Calcular Valor
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      );
    }
    
    // @ts-ignore - Tipos não têm sobreposição
    if ((costType === 'technical_direct' || costType === 'commercial_direct') && category === 'material') {
      return (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Cálculo de Material
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="quantity"
                  name="quantity"
                  label="Quantidade"
                  type="number"
                  value={formik.values.quantity || ''}
                  onChange={formik.handleChange}
                  error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                  helperText={formik.touched.quantity && formik.errors.quantity}
                  InputProps={{ inputProps: { min: 0, step: 1 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="unitCost"
                  name="unitCost"
                  label="Custo Unitário (R$)"
                  type="number"
                  value={formik.values.unitCost || ''}
                  onChange={formik.handleChange}
                  error={formik.touched.unitCost && Boolean(formik.errors.unitCost)}
                  helperText={formik.touched.unitCost && formik.errors.unitCost}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<CalculateIcon />}
                  onClick={calculateAmount}
                  disabled={!formik.values.quantity || !formik.values.unitCost}
                >
                  Calcular Valor
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      );
    }
    
    return null;
  };
  
  // Renderizar campos de referência com base no tipo de custo
  const renderReferenceFields = () => {
    const { costType } = formik.values;
    
    if (costType === 'technical_direct') {
      return (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={formik.touched.serviceRequest && Boolean(formik.errors.serviceRequest)}>
            <InputLabel>Chamado Técnico</InputLabel>
            <Select
              id="serviceRequest"
              name="serviceRequest"
              value={formik.values.serviceRequest}
              onChange={formik.handleChange}
              label="Chamado Técnico"
              disabled={loadingServiceRequests}
            >
              <MenuItem value="">Selecione um chamado</MenuItem>
              {serviceRequests.length > 0 ? (
                serviceRequests.map((request) => (
                  <MenuItem key={request._id} value={request._id}>
                    {request.number || 'Chamado'} - {request.description || 'Sem descrição'}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  Nenhum chamado disponível
                </MenuItem>
              )}
            </Select>
            {formik.touched.serviceRequest && formik.errors.serviceRequest && (
              <FormHelperText>{formik.errors.serviceRequest}</FormHelperText>
            )}
            {loadingServiceRequests && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="caption">Carregando chamados...</Typography>
              </Box>
            )}
          </FormControl>
        </Grid>
      );
    }
    
    if (costType === 'commercial_direct') {
      return (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={formik.touched.commercialVisit && Boolean(formik.errors.commercialVisit)}>
            <InputLabel>Visita Comercial</InputLabel>
            <Select
              id="commercialVisit"
              name="commercialVisit"
              value={formik.values.commercialVisit}
              onChange={formik.handleChange}
              label="Visita Comercial"
              disabled={loadingCommercialVisits}
            >
              <MenuItem value="">Selecione uma visita</MenuItem>
              {commercialVisits.length > 0 ? (
                commercialVisits.map((visit) => (
                  <MenuItem key={visit._id} value={visit._id}>
                    {visit.number || 'Visita'} - {visit.description || 'Sem descrição'}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  Nenhuma visita disponível
                </MenuItem>
              )}
            </Select>
            {formik.touched.commercialVisit && formik.errors.commercialVisit && (
              <FormHelperText>{formik.errors.commercialVisit}</FormHelperText>
            )}
            {loadingCommercialVisits && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="caption">Carregando visitas...</Typography>
              </Box>
            )}
          </FormControl>
        </Grid>
      );
    }
    
    return null;
  };
  
  // Efeitos para carregar dados iniciais
  useEffect(() => {
    if (id && id !== 'new') {
      loadCost();
    }
    
    loadServiceRequests();
    loadCommercialVisits();
    loadTechnicians();
  }, [id]);
  
  // Efeito para pré-preencher o formulário com parâmetros da URL
  useEffect(() => {
    const commercialVisitParam = searchParams.get('commercialVisit');
    const serviceRequestParam = searchParams.get('serviceRequest');
    
    if (commercialVisitParam && !isEditMode) {
      formik.setFieldValue('commercialVisit', commercialVisitParam);
      formik.setFieldValue('costType', 'commercial_direct');
      console.log('Pré-preenchendo formulário com visita comercial:', commercialVisitParam);
    }
    
    if (serviceRequestParam && !isEditMode) {
      formik.setFieldValue('serviceRequest', serviceRequestParam);
      formik.setFieldValue('costType', 'technical_direct');
      console.log('Pré-preenchendo formulário com chamado técnico:', serviceRequestParam);
    }
  }, [searchParams, isEditMode]);
  
  return (
    <AuthLayout title="Formulário de Custo">
      <Box sx={{ p: 3 }}>
        {/* Breadcrumbs de navegação */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            component="button" 
            variant="body2" 
            onClick={() => navigate('/costs')}
            underline="hover"
            color="inherit"
          >
            Custos
          </Link>
          <Typography color="text.primary">
            {isEditMode ? 'Editar Custo' : 'Novo Custo'}
          </Typography>
        </Breadcrumbs>

        {/* Título e botão de voltar */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/costs')}
            sx={{ mr: 2 }}
          >
            Voltar
          </Button>
          <Typography variant="h5">
            {isEditMode ? 'Editar Custo' : 'Novo Custo'}
          </Typography>
        </Box>

        {/* Mensagens de erro ou sucesso */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Custo salvo com sucesso!
          </Alert>
        )}

        {initialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ p: 3 }}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                {/* Tipo de Custo */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={formik.touched.costType && Boolean(formik.errors.costType)}>
                    <InputLabel>Tipo de Custo</InputLabel>
                    <Select
                      id="costType"
                      name="costType"
                      value={formik.values.costType}
                      onChange={formik.handleChange}
                      label="Tipo de Custo"
                    >
                      {Object.entries(COST_TYPE_LABELS).map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </Select>
                    {formik.touched.costType && formik.errors.costType && (
                      <FormHelperText>{formik.errors.costType}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                {/* Categoria */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={formik.touched.category && Boolean(formik.errors.category)}>
                    <InputLabel>Categoria</InputLabel>
                    <Select
                      id="category"
                      name="category"
                      value={formik.values.category}
                      onChange={formik.handleChange}
                      label="Categoria"
                    >
                      <MenuItem value="">Selecione uma categoria</MenuItem>
                      {Object.entries(COST_CATEGORY_LABELS).map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </Select>
                    {formik.touched.category && formik.errors.category && (
                      <FormHelperText>{formik.errors.category}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                {/* Campos de referência (Chamado Técnico ou Visita Comercial) */}
                {renderReferenceFields()}
                
                {/* Campo para selecionar técnico responsável */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      id="technician"
                      options={technicians}
                      loading={loadingTechnicians}
                      getOptionLabel={(option) => option.displayName || ''}
                      value={technicians.find(tech => tech._id === formik.values.technician) || null}
                      onChange={(_, newValue) => {
                        formik.setFieldValue('technician', newValue?._id || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Técnico Responsável"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {loadingTechnicians ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                
                {/* Descrição */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="description"
                    name="description"
                    label="Descrição"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    error={formik.touched.description && Boolean(formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                  />
                </Grid>
                
                {/* Data */}
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data"
                      value={formik.values.date ? new Date(formik.values.date) : null}
                      onChange={(date) => formik.setFieldValue('date', date ? date.toISOString() : null)}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true,
                          error: formik.touched.date && Boolean(formik.errors.date),
                          helperText: formik.touched.date && formik.errors.date
                        } 
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                
                {/* Valor */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="amount"
                    name="amount"
                    label="Valor (R$)"
                    type="number"
                    value={formik.values.amount}
                    onChange={formik.handleChange}
                    error={formik.touched.amount && Boolean(formik.errors.amount)}
                    helperText={formik.touched.amount && formik.errors.amount}
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                  />
                </Grid>
                
                {/* Campos específicos para cálculo */}
                <Grid item xs={12}>
                  {renderSpecificFields()}
                </Grid>
                
                {/* Observações */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="notes"
                    name="notes"
                    label="Observações"
                    multiline
                    rows={4}
                    value={formik.values.notes || ''}
                    onChange={formik.handleChange}
                    error={formik.touched.notes && Boolean(formik.errors.notes)}
                    helperText={formik.touched.notes && formik.errors.notes}
                  />
                </Grid>
                
                {/* Botões */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => navigate('/costs')}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <CircularProgress size={24} sx={{ mr: 1 }} />
                          Salvando...
                        </>
                      ) : (
                        'Salvar'
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        )}
      </Box>
    </AuthLayout>
  );
};

export default CostForm;
